import { createContext, useContext } from 'react';

// Covers only the flags used by the main app tree (AppWrapper -> App -> useEhrStateHandlers).
// components/configuration (the separate /console-ui route) resolves its own flag directly via
// useFeatureFlag and is deliberately not part of this context — it renders outside AppWrapper, so
// there's nothing to add it to.
export interface AppFeatureFlags {
  shouldGetReferralModeByDateFF: boolean;
  shouldCallOptumPatientEligibilityApiFF: boolean;
  shouldCachePatientEligibilityFF: boolean;
  shouldAutoUpdatePriorAuthStatusFF: boolean;
  shouldUseLeadingReachProviderReferToName: boolean;
  shouldUseCuroNotesText: boolean;
  shouldUseNewReferralIdFormat: boolean;
  shouldUpdateSubmittedReferralMapOnResubmit: boolean;
}

// Mirrors this monorepo's established pattern for a component tree with several consumers of the
// same up-front-resolved flags (see order-assist's OrderAssistAppFeatureFlagsContext, gaps'
// appFeatureFlagsSlice) — a dedicated context, kept separate from other app state, rather than
// threading the flags through function/prop arguments. Kept in its own module (no
// @getvim/feature-flags import) so consumers don't have to pull in the feature-flags client just
// to read already-resolved values.
//
// No default flag values here on purpose: a consumer rendered outside AppFeatureFlagsProvider must
// fail loudly (see the throw below), not silently read all-false and, for the 4 writeback flags,
// write wrong data to the persisted Optum notes/referral-ID/referTo-name.
const AppFeatureFlagsContext = createContext<AppFeatureFlags | undefined>(undefined);

export const AppFeatureFlagsProvider = AppFeatureFlagsContext.Provider;

export const useAppFeatureFlags = (): AppFeatureFlags => {
  const flags = useContext(AppFeatureFlagsContext);
  if (!flags) {
    throw new Error('useAppFeatureFlags must be used within an AppFeatureFlagsProvider');
  }
  return flags;
};
